import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, IconButton, Link, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router";
import {
  AutorenewOutlined,
  CloseRounded,
  DeleteOutlineOutlined,
  EditOutlined,
  FolderOutlined,
  OpenInFullOutlined,
  RestartAltOutlined,
  ZoomInMap,
  ZoomOutMap,
} from "@mui/icons-material";
import AssetSinglePreview, {
  PreviewContext,
} from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";

import { useSelector } from "react-redux";
import Panel from "../Panel/panel";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { SetPlayerHeight, SetPlayerResponsive } from "../../../Redux/Reducers/redux-slice-layout";
import EmptyListSlot from "../../Misc/EmptyListSlot";
import { GetPanelObject, ResetPanelObject, SetPanelObject } from "../../../Redux/Reducers/redux-slice-drive";
import { assetService, ingestService, mediaService, workflowService } from "../../../Core/Api/services";
import { routePaths } from "../../../Router/router";
import { AssetTypeAlphaColor, UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import { GetGraphResponse } from "../../../Core/Api/Types/get-graph-response";
import AssetStatus from "../../Misc/Status/AssetStatus/asset-status";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { AssetPanelDto } from "../../../Core/Api/Types/db-asset-object-infos";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import ModalRequestPurgeOpenButton from "../../Modals/ModalPurgeAsset/modal-request-purge-open-button";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import ModalConfirmActionOpenButton from "../../Modals/ModalConfirmAction/modal-confirm-action-open-button";
import ModalOrderFileFixingAction from "../../Modals/ModalOrderFileFixing/modal-order-file-fixing-action";
import ModalOrderFileFixingOpenButton from "../../Modals/ModalOrderFileFixing/modal-order-file-fixing-open-button";
import { ModalContext } from "../../Modals";
import ModalEditMetadata, { ModalEditMetadataElementProps } from "../../Modals/ModalEditMetadata/modal-edit-metadata";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { SetActiveTimeline, SetFocusedTimelineClip, TimelineType } from "../../../Redux/Reducers/redux-slice-timeline";
import { ConvertAssetClipToClipCard } from "../../Cards/ClipCard/clip-card";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { ObjectClass } from "../../../Core/Api/Types/object-class";

export interface AssetPanelProps {
  open: boolean;
  onClose: () => void;
  toggleEnlargePanel?: () => void;
  context: PreviewContext;
  closable?: boolean;
  assetId?: number;
}

export const AssetPanel2: React.FC<AssetPanelProps> = (props: AssetPanelProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { open, onClose, toggleEnlargePanel, assetId, closable, context } = props;
  const panelObj = useSelector(GetPanelObject);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(SetPlayerHeight(null));
    dispatch(SetPlayerResponsive(true));

    // If panelObj is not defined, fetch it and dispatch it to the store for other components to consume
    if (!panelObj && assetId && assetId > 0) {
      assetService.GetObjectLightById(assetId).then((asset) => {
        if (asset.graph !== null) {
          // needed for other preview components (tabs, player, etc.)
          dispatch(SetPanelObject(asset.graph));
        }
      });
    }
  }, [panelObj, assetId]);

  const handleOpenClip = useCallback(() => {
    if (!panelObj) return;
    const frameRate = GetMetadataByTagName(panelObj, "frameRate");
    const clip = ConvertAssetClipToClipCard(panelObj, frameRate);
    console.log("open clip details", clip);
    dispatch(SetActiveTimeline(TimelineType.Clips));
    dispatch(SetFocusedTimelineClip(clip));

    navigate(
      panelObj.classification === ObjectClass.Drive
        ? `${routePaths.drive.path}/${panelObj.parentObjectId}`
        : `${routePaths.asset.path}/${panelObj.parentObjectId}/preview`
    );
  }, [panelObj]);

  const panelHeaderAction = useMemo(() => {
    switch (context) {
      case PreviewContext.DriveFolderPreview:
        return (
          <Link
            href="#"
            underline="hover"
            variant="body1"
            color="primary.text"
            sx={{ flexGrow: 0 }}
            onClick={toggleEnlargePanel}
          >
            <ZoomOutMap sx={{ mr: 0.5 }} />
            {t("common:enlarge")}
          </Link>
        );
      case PreviewContext.DistribInventoryPreview:
      case PreviewContext.DistribCollectionPreview:
      case PreviewContext.DriveCollectionPreview:
        if (panelObj?.layer === ObjectLayer.Asset) {
          return (
            <Link
              to={
                context === PreviewContext.DriveCollectionPreview
                  ? `${routePaths.drive.path}/${panelObj.id}`
                  : `${routePaths.asset.path}/${panelObj.id}`
              }
              underline="hover"
              variant="body1"
              color="primary.text"
              sx={{ flexGrow: 0 }}
              component={RouterLink}
            >
              <OpenInFullOutlined sx={{ mr: 0.5 }} />
              {t("common:open")}
            </Link>
          );
        }
        if (panelObj?.layer === ObjectLayer.Clip) {
          return (
            <Link underline="hover" variant="body1" color="primary.text" sx={{ flexGrow: 0 }} onClick={handleOpenClip}>
              <OpenInFullOutlined sx={{ mr: 0.5 }} />
              {t("common:open")}
            </Link>
          );
        }
    }
  }, [panelObj]);

  if (!panelObj && open)
    return (
      <Panel open={true}>
        <Stack height="100%" justifyContent="center" alignItems="center">
          <EmptyListSlot
            title={t("component:emptyListSlot.drivePanel")}
            icon={<FolderOutlined />}
            sx={{ textAlign: "center", background: "none" }}
          />
        </Stack>
      </Panel>
    );
  else if (!panelObj) return null;
  else {
    return (
      <Panel
        header={
          <>
            {panelObj?.type !== ObjectType.Folder && (
              <Stack
                direction="row"
                alignItems={"center"}
                px={1}
                flexWrap="nowrap"
                width="100%"
                height="48px"
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
              >
                {closable && (
                  <IconButton size="small" color="ghost" aria-label="close" onClick={onClose}>
                    <CloseRounded />
                  </IconButton>
                )}
                <Stack direction="row" flexGrow={1} sx={{ overflow: "hidden", mr: 2, ml: 1, alignItems: "center" }}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    color="text.primary"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {panelObj?.name}
                  </Typography>
                </Stack>
                {panelHeaderAction}
              </Stack>
            )}
          </>
        }
        noWrapper
        open={open}
        reload={panelObj?.id}
      >
        <Box
          sx={{
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflowY: "scroll",
              ...theme.helpers(theme).scrollBar,
              ...theme.helpers(theme).scrollableContainer,
              height: "calc(100% - 48px)",
            }}
          >
            <AssetSinglePreview context={context} />
            {/* {(context === PreviewContext.DistribCollectionPreview ||
              context === PreviewContext.DistribInventoryPreview) && (
              <>
                <Stack
                  sx={{
                    p: 3,
                    background: `radial-gradient(59.89% 124.74% at 36.7% 64.33%,${AssetTypeAlphaColor(
                      panelObj.type,
                      0.1
                    )} 0%, ${AssetTypeAlphaColor(panelObj.type, 0)} 100%), ${AssetTypeAlphaColor(panelObj.type, 0.05)}`,
                  }}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h6" color="text.tier" mb={0.5}>
                      {t("common:assetIdLowerCase")}
                    </Typography>
                    <Typography variant="h3" component="h2" mb={1.5}>
                      #{panelObj.id}
                    </Typography>
                    <AssetStatus status={panelObj.status} />
                  </Box>
                  <AssetThumbnail size="large" type={panelObj.type} />
                </Stack>
                <AssetPanelRejectionActions assetId={panelObj.id} />
              </>
            )} */}
          </Box>
        </Box>
      </Panel>
    );
  }
};

// const AssetPanelRejectionActions = (props: { assetId: number }) => {
//   const { assetId } = props;
//   const [assetDto, setAssetDto] = useState<AssetPanelDto | undefined>(undefined);
//   const { t } = useTranslation();
//   const enqueueCustomSnackbar = useCustomSnackbar();
//   const dispatch = useAppDispatch();
//   const modalContext = useContext(ModalContext);
//   const { openModal } = { ...modalContext };

//   useEffect(() => {
//     assetService.GetAssetForAssetPanel(assetId).then((assetDto) => {
//       setAssetDto(assetDto);
//     });
//   }, [assetId]);

//   const handleEditMetadata = () => {
//     if (!openModal || !assetId) return;
//     openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [asset] });
//   };

//   const handleDeleteAsset = (comment?: string) => {
//     ingestService.DeleteRequest({ id: assetId, comment: comment || "" }).then((response) => {
//       if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
//         enqueueCustomSnackbar(response.message!, "primary");
//       else enqueueCustomSnackbar(t("alert:success.assetDeleted", { id: assetId }), "success");
//       dispatch(ResetPanelObject());
//     });
//   };

//   const handleBypassRejection = (comment?: string) => {
//     workflowService.AssetByPassRejection(assetId, comment).then((response) => {
//       if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
//         enqueueCustomSnackbar(response.message!, "primary");
//       else enqueueCustomSnackbar(t("alert:success.assetRejectionBypassed", { id: assetId }), "success");
//     });
//   };

//   if (assetDto === undefined) return <Stack>Hello</Stack>;
//   return (
//     <Stack p={2}>
//       {(assetDto.status === ObjectStatus.Rejected || assetDto.status === ObjectStatus.RejectedAuto) && (
//         <>
//           <Stack spacing={1}>
//             <MetadataListTextItem title={t("menu:title.rejectedBy")}>
//               <Link color="text.primary" underline="hover">
//                 {assetDto.rejectedBy}
//               </Link>
//             </MetadataListTextItem>
//             <MetadataListTextItem title={t("menu:title.rejectedAt")}>{assetDto.rejectedAt}</MetadataListTextItem>
//           </Stack>
//           <Typography mt={2} color="error">
//             {assetDto.rejectedComment}
//           </Typography>
//           <Typography mt={2} variant="h6" color="text.placeholder">
//             {t("panel:assetPanel.body.resolvingActions")}
//           </Typography>
//           <Grid container spacing={1} mt={1.5}>
//             <Grid item xs={6}>
//               <ModalRequestPurgeOpenButton
//                 title={t("modal:title.confirmMediaPurgeAction")}
//                 assetIds={[assetDto.id]}
//                 startIcon={<RestartAltOutlined />}
//                 fullWidth
//                 size="small"
//                 disabled={!assetDto.userCanPurge}
//               >
//                 {t("modal:body.purgeMediaFiles")}
//               </ModalRequestPurgeOpenButton>
//             </Grid>
//             <Grid item xs={6}>
//               <Button
//                 fullWidth
//                 size="small"
//                 startIcon={<EditOutlined />}
//                 onClick={handleEditMetadata}
//                 disabled={!UserHasAcl(AclTypes.aclMetadataUpdate)}
//               >
//                 {t("common:editMetadata")}
//               </Button>
//             </Grid>
//             <Grid item xs={6}>
//               <ModalConfirmActionOpenButton
//                 displayComment
//                 modalMessage={t("modal:title.confirmAssetDeletion")}
//                 startIcon={<DeleteOutlineOutlined />}
//                 onConfirm={(comment?: string) => handleDeleteAsset(comment)}
//                 fullWidth
//                 size="small"
//                 color={"error"}
//                 disabled={!UserHasAcls([AclTypes.aclIngestDeleteRequest, AclTypes.aclElevationWishlistCreate])}
//               >
//                 {t("modal:body.deleteAsset")}
//               </ModalConfirmActionOpenButton>
//             </Grid>
//             <Grid item xs={6}>
//               <ModalConfirmActionOpenButton
//                 displayComment
//                 modalMessage={t("modal:title.confirmBypassRejection")}
//                 startIcon={<AutorenewOutlined />}
//                 onConfirm={(comment?: string) => handleBypassRejection(comment)}
//                 fullWidth
//                 size="small"
//                 color={"error"}
//                 disabled={assetDto.userCanBypassReject}
//               >
//                 {t("modal:body.bypassRejection")}
//               </ModalConfirmActionOpenButton>
//             </Grid>
//             <Grid item xs={12}>
//               <ModalOrderFileFixingOpenButton fullWidth size="small" objectId={assetDto.id} />
//             </Grid>
//           </Grid>
//         </>
//       )}
//       {assetDto.status === ObjectStatus.Deleted && (
//         <>
//           <Stack spacing={1} mt={3}>
//             <MetadataListItem title={t("menu:title.deletedBy")}>
//               <Link color="text.primary" underline="hover">
//                 {assetDto.deletedBy}
//               </Link>
//             </MetadataListItem>
//             <MetadataListTextItem title={t("menu:title.deletedAt")}>{assetDto.deletedAt}</MetadataListTextItem>
//           </Stack>
//           <Typography mt={2} color="text.secondary">
//             {assetDto.deletedComment}
//           </Typography>
//         </>
//       )}
//       {assetDto.status === ObjectStatus.Accepted && assetDto.isBypassed && (
//         <>
//           <Stack spacing={1} mt={3}>
//             <MetadataListItem title={t("menu:title.bypassedBy")}>
//               <Link color="text.primary" underline="hover">
//                 {assetDto.bypassedBy}
//               </Link>
//             </MetadataListItem>
//             <MetadataListTextItem title={t("menu:title.bypassedAt")}>{assetDto.bypassedAt}</MetadataListTextItem>
//           </Stack>
//           <Typography mt={2} color="warning.main">
//             {assetDto.bypassedComment}
//           </Typography>
//         </>
//       )}
//     </Stack>
//   );
// };
